import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
// Single source of truth for the supported locale set. The backend FE/BE contract
// test (LocaleContractTest) reads this same file, so what the frontend can emit can
// never drift from what the backend recognizes.
import supportedLocales from "./locales/supported-locales.json";

// i18next resources are built from every locale folder present. Adding a new
// locales/<code>/translation.json is sufficient for i18next to serve it — no edit here.
// import.meta.glob keys by full path with a { default } module wrapper, so extract the
// folder code from the path and unwrap .default into the i18next `translation` namespace.
const translationModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "./locales/*/translation.json",
  { eager: true },
);
const resources = Object.fromEntries(
  Object.entries(translationModules).map(([path, mod]) => [
    path.split("/").at(-2) as string,
    { translation: mod.default },
  ]),
);

// Sync HTML lang attribute with i18next language (registered before init to capture initial event)
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

// Story 28-4: keep the API client's locale in lockstep with i18n.
//
// This originally existed because the client prefixed every URL with the locale. That prefix is
// gone (docs/locale-removal-plan.md, step 3), and T3.3 called for deleting this listener with it —
// but it still earns its place, for a narrower reason. The two calls that create and complete a
// form send `?lang=`, and the server persists that as the form's submittedLocale, which decides
// what language the guest's emails are written in from then on. `?lang=` reads the client's
// `language` field, and this listener is the only thing that keeps it current. Without it, a guest
// who switches to German gets a German UI and English mail.
//
// Late-bound via dynamic import so this module stays import-cycle-free with services/api/client.
i18n.on('languageChanged', (lng) => {
  void import('@/services/api/client').then(({ apiClient }) => {
    apiClient.updateLanguage(lng);
  });
});

i18n
  // detect user language
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next
  .use(initReactI18next)
  // init i18next
  .init({
    resources,
    // Constrain detected language to what the backend actually supports.
    // Without this, a Portuguese phone produces i18n.language === "pt-BR",
    // which the API client forwards as /v1/pt/... → API Gateway 401 (no
    // matching route), surfaced to the user as a generic "Something went
    // wrong" card with no Sentry event (4xx are not captured).
    supportedLngs: supportedLocales,
    load: "languageOnly",
    fallbackLng: "en",
    debug: process.env.NODE_ENV === "test",
    // Story 28-4: detection order — read the LanguageToggle's
    // localStorage key BEFORE the URL path / browser locale. Without this,
    // a guest who chose "ES" yesterday on this device would see "EN" again
    // on a fresh visit if the URL or browser detector picks "en" first.
    detection: {
      order: ["localStorage", "path", "navigator", "htmlTag"],
      lookupLocalStorage: "preferredLang",
      caches: ["localStorage"],
    },

    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

// Fallback: ensure lang is set if init resolved synchronously before handler
document.documentElement.lang = i18n.language || 'en';

export default i18n;
